import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, RefreshCw, AlertTriangle, X, CheckCircle, Shield } from 'lucide-react';
import MainService from '../../services/MainService';
import { mapVehicleDropdownList, isApiSaveSuccess } from '../../utils/vehicleApiUtils';

const AddInsurance = () => {
  const { vehicleId, insuranceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [insurance, setInsurance] = useState({
    Id: 0,
    VehicleId: vehicleId || '',
    InsuranceCompany: '',
    PolicyNumber: '',
    PolicyType: '',
    StartDate: '',
    ExpiryDate: '',
    PremiumAmount: '',
    CoverageAmount: '',
    AgentName: '',
    AgentContact: '',
    Notes: ''
  });
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    loadVehicles();
    if (insuranceId) {
      loadInsurance();
    }
  }, [insuranceId]);

  useEffect(() => {
    if (!insuranceId && vehicleId) {
      setInsurance((prev) => ({ ...prev, VehicleId: vehicleId }));
    }
  }, [vehicleId, insuranceId]);

  const loadVehicles = async () => {
    try {
      const response = await MainService.VehiclesInDropDown();
      setVehicles(mapVehicleDropdownList(response));
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setVehicles([]);
    }
  };

  const loadInsurance = async () => {
    try {
      setLoading(true);
      const row = await MainService.VehicleInsuranceById(insuranceId);
      if (row) {
        setInsurance({
          Id: row.Id ?? row.ID ?? 0,
          VehicleId: String(row.VehicleId ?? row.VehicleID ?? vehicleId ?? ''),
          InsuranceCompany: row.InsuranceCompany || '',
          PolicyNumber: row.PolicyNumber || '',
          PolicyType: row.PolicyType || '',
          StartDate: row.StartDate || '',
          ExpiryDate: row.ExpiryDate || '',
          PremiumAmount: row.PremiumAmount != null ? String(row.PremiumAmount) : '',
          CoverageAmount: row.CoverageAmount != null ? String(row.CoverageAmount) : '',
          AgentName: row.AgentName || '',
          AgentContact: row.AgentContact || '',
          Notes: row.Notes || ''
        });
      }
    } catch (err) {
      console.error('Error loading insurance:', err);
      setError('Failed to load insurance record');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInsurance(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!insurance.InsuranceCompany) {
      setError('Please enter insurance company');
      return;
    }
    if (!insurance.PolicyNumber) {
      setError('Please enter policy number');
      return;
    }
    if (!insurance.StartDate) {
      setError('Please select start date');
      return;
    }
    if (!insurance.ExpiryDate) {
      setError('Please select expiry date');
      return;
    }
    if (!insurance.PremiumAmount) {
      setError('Please enter premium amount');
      return;
    }

    const vid = Number(insurance.VehicleId || vehicleId);
    if (!vid) {
      setError('Please select vehicle');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const payload = {
        Id: Number(insurance.Id) || 0,
        VehicleId: vid,
        InsuranceCompany: insurance.InsuranceCompany,
        PolicyNumber: insurance.PolicyNumber,
        PolicyType: insurance.PolicyType || '',
        StartDate: insurance.StartDate,
        ExpiryDate: insurance.ExpiryDate,
        PremiumAmount: Number(insurance.PremiumAmount),
        CoverageAmount:
          insurance.CoverageAmount === '' || insurance.CoverageAmount == null
            ? null
            : Number(insurance.CoverageAmount),
        AgentName: insurance.AgentName || '',
        AgentContact: insurance.AgentContact || '',
        Notes: insurance.Notes || ''
      };
      const response = await MainService.SaveVehicleInsurance(payload);
      if (!isApiSaveSuccess(response)) {
        setError('Failed to save insurance record');
        return;
      }
      setSuccess('Insurance record saved successfully');
      setTimeout(() => {
        navigate(
          vehicleId
            ? `/home/vehicle-management/insurance/${vehicleId}`
            : '/home/vehicle-management/insurance'
        );
      }, 800);
    } catch (err) {
      console.error('Error saving insurance:', err);
      setError('Failed to save insurance record');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setInsurance({
      Id: 0,
      VehicleId: vehicleId || '',
      InsuranceCompany: '',
      PolicyNumber: '',
      PolicyType: '',
      StartDate: '',
      ExpiryDate: '',
      PremiumAmount: '',
      CoverageAmount: '',
      AgentName: '',
      AgentContact: '',
      Notes: ''
    });
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">
              {insuranceId ? 'Edit Insurance' : 'Add Insurance'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>Save</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 ml-8">
          {insuranceId ? 'Update insurance information' : 'Add a new insurance policy'}
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Vehicle */}
          {!vehicleId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle <span className="text-red-500">*</span>
              </label>
              <select
                name="VehicleId"
                value={insurance.VehicleId}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
              >
                <option value="">Select Vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.Id} value={vehicle.Id}>
                    {vehicle.PlateNumber || vehicle.Number || vehicle.Name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Insurance Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Insurance Company <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="InsuranceCompany"
              value={insurance.InsuranceCompany}
              onChange={handleInputChange}
              placeholder="Enter insurance company"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Policy Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Policy Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="PolicyNumber"
              value={insurance.PolicyNumber}
              onChange={handleInputChange}
              placeholder="Enter policy number"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Policy Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Policy Type</label>
            <input
              type="text"
              name="PolicyType"
              value={insurance.PolicyType}
              onChange={handleInputChange}
              placeholder="Enter policy type"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="StartDate"
              value={insurance.StartDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!insurance.StartDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Expiry Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiry Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="ExpiryDate"
              value={insurance.ExpiryDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!insurance.ExpiryDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Premium Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Premium Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="PremiumAmount"
              value={insurance.PremiumAmount}
              onChange={handleInputChange}
              placeholder="Enter premium amount"
              step="0.01"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Coverage Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Coverage Amount</label>
            <input
              type="number"
              name="CoverageAmount"
              value={insurance.CoverageAmount}
              onChange={handleInputChange}
              placeholder="Enter coverage amount"
              step="0.01"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Agent Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agent Name</label>
            <input
              type="text"
              name="AgentName"
              value={insurance.AgentName}
              onChange={handleInputChange}
              placeholder="Enter agent name"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Agent Contact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agent Contact</label>
            <input
              type="text"
              name="AgentContact"
              value={insurance.AgentContact}
              onChange={handleInputChange}
              placeholder="Enter agent contact"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>

          {/* Notes */}
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="Notes"
              value={insurance.Notes}
              onChange={handleInputChange}
              placeholder="Enter additional notes"
              rows="3"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInsurance;

